// src/pages/lkp/EntityHarmonizePanel.tsx
//
// On-demand entity-name harmonization. Opens as a modal, proposes clusters
// (deterministic), lets the SME run the agent semantic pass for synonyms, and
// applies ONLY the clusters the SME approves. Reusable across the whole registry.

import React, { useCallback, useEffect, useState } from "react";
import {
  lookup_harmonizePropose, lookup_harmonizeAgent, lookup_harmonizeApply,
  type HarmonizeCluster,
} from "../api/lkpEntityAPI";

interface Props { entityId: string; onApplied: () => void; onClose: () => void; }

export default function EntityHarmonizePanel({ entityId, onApplied, onClose }: Props) {
  const [clusters, setClusters] = useState<HarmonizeCluster[]>([]);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [singletons, setSingletons] = useState<number>(0);

  const key = (c: HarmonizeCluster) => c.canonical_entity_id + "|" + c.member_entity_ids.join(",");

  const load = useCallback(async () => {
    setBusy("load");
    try {
      const r = await lookup_harmonizePropose(entityId);
      const all = [...r.auto, ...r.review];
      setClusters(all); setSingletons(r.singletons.length);
      // pre-check the safe (core_match / high-confidence) ones
      const c: Record<string, boolean> = {};
      all.forEach((cl) => { c[key(cl)] = cl.method === "core_match" || (cl.confidence ?? 0) >= 0.9; });
      setChecked(c);
    } catch (e: any) { setMsg(e?.detail || "Failed to load proposals"); } finally { setBusy(null); }
  }, [entityId]);
  useEffect(() => { load(); }, [load]);

  const runAgent = async () => {
    setBusy("agent");
    try {
      const r = await lookup_harmonizeAgent(entityId);
      // append agent proposals not already present
      setClusters((prev) => {
        const have = new Set(prev.map(key));
        const add = (r.proposed || []).filter((c) => !have.has(key(c)));
        return [...prev, ...add];
      });
      setMsg(r.proposed?.length ? `Agent proposed ${r.proposed.length} more cluster(s).` : "Agent found no additional merges.");
    } catch (e: any) { setMsg(e?.detail || "Agent pass unavailable (not wired)."); } finally { setBusy(null); }
  };

  const apply = async () => {
    const selected = clusters.filter((c) => checked[key(c)]);
    if (!selected.length) { setMsg("Nothing selected."); return; }
    setBusy("apply");
    try {
      const r = await lookup_harmonizeApply(entityId, selected);
      setMsg(`Merged ${r.entities_merged} entit${r.entities_merged === 1 ? "y" : "ies"}.`);
      onApplied(); onClose();
    } catch (e: any) { setMsg(e?.detail || "Apply failed"); } finally { setBusy(null); }
  };

  const nSel = clusters.filter((c) => checked[key(c)]).length;
  const methodMeta = (m?: string) => m === "core_match" ? { t: "exact", c: "var(--gov)" }
    : m === "agent_semantic" ? { t: "agent", c: "var(--infer)" } : { t: "similar", c: "var(--wait)" };

  return (
    <div className="ehp-scrim" onClick={onClose}>
      <style>{CSS}</style>
      <div className="ehp" onClick={(e) => e.stopPropagation()}>
        <div className="ehp-head">
          <div><div className="ehp-title">Harmonize entity names</div>
            <div className="ehp-sub">Merge entities that mean the same thing. Only checked clusters are applied.</div></div>
          <button className="ehp-x" onClick={onClose}>✕</button>
        </div>

        <div className="ehp-bar">
          <button className="ehp-btn ghost" disabled={!!busy} onClick={runAgent}>{busy === "agent" ? "Asking agent…" : "Run agent semantic pass"}</button>
          <div className="ehp-sp" />
          {msg && <span className="ehp-msg">{msg}</span>}
        </div>

        <div className="ehp-body">
          {busy === "load" ? <div className="ehp-empty">Analyzing entity names…</div>
            : clusters.length === 0 ? <div className="ehp-empty">No merge candidates. {singletons} entit{singletons === 1 ? "y" : "ies"} already distinct.</div>
              : clusters.map((c) => {
                const k = key(c); const mm = methodMeta(c.method);
                return (
                  <label key={k} className={"ehp-cl " + (checked[k] ? "on" : "")}>
                    <input type="checkbox" checked={!!checked[k]} onChange={(e) => setChecked((p) => ({ ...p, [k]: e.target.checked }))} />
                    <div className="ehp-cl-body">
                      <div className="ehp-cl-top">
                        <span className="ehp-canon">{c.canonical_name}</span>
                        <span className="ehp-method" style={{ color: mm.c, borderColor: mm.c }}>{mm.t}</span>
                        {c.confidence != null && <span className="ehp-conf">{Math.round(c.confidence * 100)}%</span>}
                      </div>
                      <div className="ehp-members">
                        merges: {(c.members || []).map((m, i) => (
                          <span key={m.entity_id} className="ehp-mem">{m.name}<span className="ehp-mb">{m.bindings}f</span>{i < (c.members!.length - 1) ? "" : ""}</span>
                        ))}
                      </div>
                    </div>
                  </label>
                );
              })}
        </div>

        <div className="ehp-foot">
          <span className="ehp-hint">Checked clusters merge all member fields under one entity. Nothing else changes.</span>
          <div className="ehp-sp" />
          <button className="ehp-btn line" disabled={!!busy} onClick={onClose}>Cancel</button>
          <button className="ehp-btn pri" disabled={!!busy || nSel === 0} onClick={apply}>{busy === "apply" ? "Applying…" : `Apply ${nSel} merge${nSel === 1 ? "" : "s"}`}</button>
        </div>
      </div>
    </div>
  );
}

const CSS = `
.ehp-scrim{position:fixed;inset:0;background:#14202b66;z-index:70;display:flex;align-items:center;justify-content:center;
  --gov:#0e8a6e;--gov-bg:#e2f2ec;--wait:#bd7a15;--infer:#4a51c0;--line:#dde3df;--tx:#182430;--tx2:#5d6d68;--tx3:#8a9791;--surface2:#f7f9f7}
.ehp{width:min(620px,94vw);max-height:88vh;background:#fff;border-radius:14px;box-shadow:0 20px 60px #14202b40;display:flex;flex-direction:column;
  font-family:'Inter',system-ui,sans-serif;color:var(--tx)}
.ehp *{box-sizing:border-box}
.ehp-head{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;padding:18px 20px;border-bottom:1px solid var(--line)}
.ehp-title{font-size:17px;font-weight:600;font-family:'Space Grotesk',Inter,sans-serif}
.ehp-sub{font-size:12.5px;color:var(--tx2);margin-top:3px}
.ehp-x{border:none;background:none;font-size:16px;cursor:pointer;color:var(--tx3)}
.ehp-bar{display:flex;align-items:center;gap:10px;padding:11px 20px;background:var(--surface2);border-bottom:1px solid var(--line)}
.ehp-sp{flex:1}.ehp-msg{font-size:12px;color:var(--tx2)}
.ehp-body{overflow-y:auto;padding:14px 20px;display:flex;flex-direction:column;gap:9px}
.ehp-empty{text-align:center;padding:36px;color:var(--tx3)}
.ehp-cl{display:flex;gap:11px;align-items:flex-start;border:1px solid var(--line);border-radius:11px;padding:12px 13px;cursor:pointer}
.ehp-cl.on{border-color:var(--gov);background:var(--gov-bg)}
.ehp-cl input{margin-top:3px}
.ehp-cl-body{flex:1;min-width:0}
.ehp-cl-top{display:flex;align-items:center;gap:9px}
.ehp-canon{font-weight:600;font-size:14px}
.ehp-method{font-size:10.5px;border:1px solid;border-radius:5px;padding:1px 7px;font-weight:600;text-transform:uppercase}
.ehp-conf{font-size:11px;color:var(--tx3);font-family:'IBM Plex Mono',monospace}
.ehp-members{font-size:12px;color:var(--tx2);margin-top:6px;display:flex;flex-wrap:wrap;gap:6px;align-items:center}
.ehp-mem{background:#fff;border:1px solid var(--line);border-radius:6px;padding:2px 8px;font-size:11.5px;display:inline-flex;gap:5px;align-items:center}
.ehp-mb{font-size:9.5px;color:var(--tx3);font-family:'IBM Plex Mono',monospace}
.ehp-foot{display:flex;align-items:center;gap:11px;padding:14px 20px;border-top:1px solid var(--line);flex-wrap:wrap}
.ehp-hint{font-size:11.5px;color:var(--tx3)}
.ehp-btn{border:1px solid var(--line);background:#fff;border-radius:8px;padding:8px 14px;font-size:13px;font-weight:500;cursor:pointer;font-family:inherit;color:var(--tx)}
.ehp-btn.pri{background:var(--gov);border-color:var(--gov);color:#fff}.ehp-btn.pri:hover{background:#0b7560}
.ehp-btn.ghost{background:var(--surface2)}.ehp-btn.line{background:#fff}
.ehp-btn:disabled{opacity:.5;cursor:default}
`;
